/**
 * window.HiisiUnits — site-wide metric/imperial toggle helper.
 *
 * Storage: all stored data is metric (mm, cm, kg, g, °). This helper
 * formats the displayed value for the customer's preferred unit. See
 * proposals/spec-metric-imperial-toggle.md for the full unit-by-unit
 * rules (suspension travel, geometry, rotors stay in mm regardless;
 * tire/wheel sizes stay in inches; only bike weight + body
 * measurements toggle).
 *
 * Preference resolution order on first read:
 *   1. window.HIISI_UNITS_PREF — emitted by theme.liquid from
 *      customer.metafields.hiisi_pref.units (logged-in customers).
 *   2. localStorage 'hiisi_units' — set on every toggle so anonymous
 *      visitors keep their choice across sessions.
 *   3. 'metric' — Pole is EU-based, default mirrors that.
 *
 * On flip:
 *   - localStorage updated immediately (no waiting for the AJAX).
 *   - DOM event 'hiisi-units-changed' dispatched on document so
 *     consumers (bike-card weight, mittadata-lomake) can re-render.
 *   - For logged-in customers: POST /apps/hiisi-bespoke/preferences
 *     to persist on the customer metafield. Anonymous visitors skip
 *     the AJAX (no auth) and keep just the localStorage write.
 *
 * The script is intentionally vanilla (no React, no module bundling)
 * so it can ship via the same theme assets pipeline as the rest of
 * bespoke-*.js. Loaded with `defer` from theme.liquid right after
 * window.HIISI_UNITS_PREF is emitted.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'hiisi_units';
  var EVENT_NAME = 'hiisi-units-changed';
  var ENDPOINT = '/apps/hiisi-bespoke/preferences';
  var ALLOWED = ['metric', 'imperial'];

  // ─── SHELVED 2026-04-26 ──────────────────────────────────────────────────
  // Toggle UI is hidden in sections/header.liquid + bespoke-configurator.liquid
  // pending UX rework. The helper still loads (consumers like configurator
  // React + my-hiisi snippets call .format() and converters) but state is
  // pinned to 'metric' so every read returns kg/cm/mm/° regardless of any
  // stale localStorage / customer-metafield value.
  //
  // Re-enable steps:
  //   1. Restore resolveInitial() body (delete the early `return 'metric'`).
  //   2. Restore setCurrent() body (delete the early `return`).
  //   3. Un-comment {%- render 'hiisi-units-selector' -%} in
  //      sections/header.liquid + sections/bespoke-configurator.liquid.
  //
  // See proposals/handout-shelve-units-toggle-temporarily.md for the full
  // re-enable checklist + tests.
  // ─────────────────────────────────────────────────────────────────────────

  // Resolve the initial value once. Reading localStorage on every getter
  // call would still be cheap, but caching here means consumers can grab
  // HiisiUnits.current in a hot loop without thrashing.
  function resolveInitial() {
    return 'metric';  // SHELVED — see banner above. Original logic below.
    /*
    var server = (typeof window !== 'undefined' && window.HIISI_UNITS_PREF) || null;
    if (ALLOWED.indexOf(server) !== -1) return server;
    try {
      var stored = window.localStorage && window.localStorage.getItem(STORAGE_KEY);
      if (ALLOWED.indexOf(stored) !== -1) return stored;
    } catch (_e) {
      // Private mode / disabled storage — fall through.
    }
    return 'metric';
    */
  }

  var state = {
    current: resolveInitial(),
  };

  function persistAjax(value) {
    // Anonymous visitor — App Proxy will reject without logged_in_customer_id,
    // so don't even fire the request. The localStorage write covers them.
    var loggedIn = document.documentElement.getAttribute('data-customer-logged-in') === 'true'
      || (typeof window !== 'undefined' && window.HIISI_CUSTOMER_LOGGED_IN === true);
    if (!loggedIn) return Promise.resolve(null);

    return fetch(ENDPOINT, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ pref: 'units', value: value }),
    }).then(function (res) {
      // Failure is non-fatal — localStorage already has the value, the
      // customer keeps the toggle until next login on a different device.
      // The next /preferences POST (on next toggle) will succeed.
      if (!res.ok) {
        try { console.warn('[HiisiUnits] /preferences POST failed:', res.status); } catch (_e) {}
      }
      return res;
    }).catch(function (err) {
      try { console.warn('[HiisiUnits] /preferences POST errored:', err); } catch (_e) {}
      return null;
    });
  }

  function setCurrent(value) {
    return;  // SHELVED 2026-04-26 — no-op while UI is hidden. Defends against
             // programmatic flips that would otherwise write localStorage,
             // dispatch events, and POST to /apps/hiisi-bespoke/preferences.
             // Re-enable: delete this early return.
    /* original implementation (re-enable: delete the early return above):
    if (ALLOWED.indexOf(value) === -1) return;
    if (state.current === value) return;
    state.current = value;
    try {
      window.localStorage && window.localStorage.setItem(STORAGE_KEY, value);
    } catch (_e) {}

    // Update toggle DOM if present — the click handler relies on this so a
    // programmatic HiisiUnits.current = '...' also reflects in the UI.
    syncToggleDom(value);

    // Tell consumers. We pass both the new value and the previous so a
    // listener can decide whether to re-render only on flip vs. on every
    // resolve.
    document.dispatchEvent(new CustomEvent(EVENT_NAME, {
      detail: { value: value },
    }));

    persistAjax(value);
    */
  }

  function syncToggleDom(value) {
    var label = value === 'imperial' ? 'Imperial' : 'Metric';
    var toggles = document.querySelectorAll('[data-units-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      var t = toggles[i];
      // Summary trigger reflects the current selection.
      var current = t.querySelector('[data-units-current]');
      if (current) current.textContent = label;
      // Menu options highlight the active choice.
      var opts = t.querySelectorAll('[data-units-opt]');
      for (var j = 0; j < opts.length; j++) {
        var opt = opts[j];
        if (opt.getAttribute('data-units-opt') === value) {
          opt.classList.add('active');
        } else {
          opt.classList.remove('active');
        }
      }
    }
  }

  // Numeric converters. Storage is always metric; these convert the metric
  // value into imperial display. Reverse converters (input forms) included
  // for the customer-body-measurements form.
  var converters = {
    cmToIn: function (cm) { return cm / 2.54; },
    inToCm: function (inches) { return inches * 2.54; },
    mmToIn: function (mm) { return mm / 25.4; },
    inToMm: function (inches) { return inches * 25.4; },
    kgToLbs: function (kg) { return kg * 2.20462; },
    lbsToKg: function (lbs) { return lbs / 2.20462; },
    gToLbs: function (g) { return g / 453.592; },
    lbsToG: function (lbs) { return lbs * 453.592; },
  };

  // Format a stored metric value into the customer's current preference.
  // fromUnit is the unit the value is currently stored in.
  // Returns { value, unit, formatted } so callers can either drop the
  // formatted string straight into markup, or use value/unit to compose
  // their own (e.g. label + delta).
  //
  // Decimals default to 1 for length, 1 for weight; pass options.decimals
  // to override. options.unitless = true skips the unit suffix.
  function format(value, fromUnit, options) {
    options = options || {};
    var decimals = typeof options.decimals === 'number' ? options.decimals : 1;
    var pref = state.current;

    function out(v, unit) {
      var rounded = Number(v.toFixed(decimals));
      var display = options.unitless ? String(rounded) : rounded + ' ' + unit;
      return { value: rounded, unit: unit, formatted: display };
    }

    if (pref === 'metric') {
      // Pass through — `value` is already in the storage unit.
      return out(value, fromUnit);
    }

    // Imperial — convert per source unit.
    switch (fromUnit) {
      case 'cm': return out(converters.cmToIn(value), 'in');
      case 'mm': return out(converters.mmToIn(value), 'in');
      case 'kg': return out(converters.kgToLbs(value), 'lbs');
      case 'g':  return out(converters.gToLbs(value), 'lbs');
      default:
        // Unitless or unknown unit — return as-is.
        return out(value, fromUnit);
    }
  }

  // Public API. Property accessor mirrors the spec example
  // (`HiisiUnits.current = 'imperial'`) so both reads and writes go
  // through the same code path.
  var api = {
    format: format,
    cmToIn: converters.cmToIn,
    inToCm: converters.inToCm,
    mmToIn: converters.mmToIn,
    inToMm: converters.inToMm,
    kgToLbs: converters.kgToLbs,
    lbsToKg: converters.lbsToKg,
    gToLbs: converters.gToLbs,
    lbsToG: converters.lbsToG,
  };
  Object.defineProperty(api, 'current', {
    get: function () { return state.current; },
    set: function (value) { setCurrent(value); },
    enumerable: true,
    configurable: false,
  });

  if (typeof window !== 'undefined') {
    window.HiisiUnits = api;
  }

  // Toggle button click handler — flips current to whichever option was
  // clicked, falls through to setCurrent which handles persistence.
  function bindToggles() {
    var toggles = document.querySelectorAll('[data-units-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      var t = toggles[i];
      if (t.dataset.unitsBound === '1') continue;
      t.dataset.unitsBound = '1';
      t.addEventListener('click', onToggleClick);
    }
  }

  function onToggleClick(event) {
    // Disclosure-style markup: clicks on <summary> are handled by the native
    // <details> element (open/close). We only act on clicks inside a menu
    // option ([data-units-opt]) — pick that value and close the menu.
    var target = event.target;
    var opt = target && target.closest && target.closest('[data-units-opt]');
    if (!opt) return;
    var value = opt.getAttribute('data-units-opt');
    if (ALLOWED.indexOf(value) === -1) return;
    setCurrent(value);
    var details = opt.closest && opt.closest('details[data-units-toggle]');
    if (details) details.open = false;
  }

  // Click-outside auto-close. <details> doesn't do this on its own — without
  // it the menu stays open after the customer clicks elsewhere on the page.
  function onDocumentClick(event) {
    var openMenus = document.querySelectorAll('details[data-units-toggle][open]');
    for (var i = 0; i < openMenus.length; i++) {
      var d = openMenus[i];
      if (!d.contains(event.target)) {
        d.open = false;
      }
    }
  }

  function init() {
    syncToggleDom(state.current);
    bindToggles();
    if (!document.documentElement.dataset.hiisiUnitsClickOutside) {
      document.documentElement.dataset.hiisiUnitsClickOutside = '1';
      document.addEventListener('click', onDocumentClick);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
